import {useOverlayActivity} from "components/useOverlayActivity";
import {useEffect, useMemo, useState} from "react";
import {overlay} from "theme/tokens";
import {SheetTransition} from "transitions/SheetTransition";
import {useLocale, useMessages} from "i18n";
import {useUser} from "UserProvider";
import {SubscriptionElementsCheckout} from "pay/SubscriptionElementsCheckout";
import {formatMoney} from "pricing/PricingStorage";
import {
	DEFAULT_EXIT_OFFER_VARIANT,
	EXIT_OFFER_INTRO_DAYS,
	EXIT_OFFER_TRIAL_DAYS,
	exitOfferMonthlyFor,
	exitOfferPlanFor,
	exitOfferPriceFor,
	exitOfferRegularPriceFor,
	exitOfferVariantsFor,
	type ExitOfferAmounts,
	type PricingExitOfferVariant,
} from "./exitOffer";
import {ImmediateDeliveryConsent} from "pay/ImmediateDeliveryConsent";
import {useCheckoutDocument} from "pay/useCheckoutDocument";
import {withdrawalEffectOf} from "pay/withdrawalRight";
import {ExitOfferArt} from "./ExitOfferArt";
import {useExitOfferPurchase} from "./useExitOfferPurchase";
import {OfferOption} from "./OfferOption";
import {Options} from "./OfferOption.styles";
import {useRadioGroupKeys} from "./useRadioGroupKeys";
import {
	AfterNote, Art, CloseButton, Content, Continue, Eyebrow, Layout, OfferDialog, OfferUnavailable, Title,
	TitleAccent,
} from "./ExitOfferDialog.styles";

/**
 * The offer a reader meets on their way out of the pricing dialog (kitecorp/transferify-ui#1722),
 * built from the owner's approved prototype of 2026-08-30.
 *
 * Whether it appears at all is not decided here — {@link ./useExitOffer} asks the React-free rule and
 * only then mounts this open. This file is the view: the terms {@link exitOfferVariantsFor} lists (two,
 * or three with the Plus first month, kitecorp/transferify-ui#2994), one CTA, and the amounts its own
 * storefront quotes.
 *
 * <b>Every amount is formatted by {@link formatMoney}</b>, the same formatter the pricing cards and
 * the first-month dialog use, so the glyph sits where the rest of the storefront puts it and the
 * Romanian brand reads "119 lei" rather than a euro figure under a leu glyph. The prototype drew the
 * euro sign after the number; this app has always put it before, and one storefront speaking about
 * the same price two ways is worse than a mock reproduced to the pixel.
 */
export function ExitOfferDialog(
	{open, onClose, amounts}: {open: boolean; onClose: () => void; amounts: ExitOfferAmounts | null},
) {
	const {exitOffer, common, checkout, pricing} = useMessages();
	const [user] = useUser();
	const observedExit = useOverlayActivity(open && amounts !== null);
	const {locale} = useLocale();
	const contract = useCheckoutDocument(open);
	const {
		continueWithOffer, offerUnavailable, continueAtRegularPrice, redirecting, checkoutVariant,
		acknowledged, setAcknowledged, admission, clientSecret, collectsShippingAddress, trialHold, error,
		dismiss: dismissPayment, completed,
	} = useExitOfferPurchase(undefined, contract.latestRead);
	const [variant, setVariant] = useState<PricingExitOfferVariant>(DEFAULT_EXIT_OFFER_VARIANT);
	// The terms on offer this showing, in the owner's order; the arrow keys walk exactly these.
	const variants = useMemo(
		() => amounts === null ? [DEFAULT_EXIT_OFFER_VARIANT] : exitOfferVariantsFor(amounts), [amounts]);

	// Re-seeded on every open rather than only on mount: the subtree can outlive one close, and a
	// reader returning should meet the offer the dialog leads with, not a choice they made last time
	// and did not act on.
	//
	// THE ACKNOWLEDGEMENT IS RE-SEEDED FOR A SHARPER REASON than the variant (kitecorp/transferify-ui#1844):
	// a box that arrives already ticked because of a previous visit is a pre-ticked box, and a pre-ticked
	// box is not consent. Carrying it across a close would void the very condition it exists to establish.
	useEffect(() => {
		if (open) {
			setVariant(DEFAULT_EXIT_OFFER_VARIANT);
			setAcknowledged(false);
		}
	}, [open, setAcknowledged]);
	useEffect(() => { if (!open) dismissPayment(); }, [open, dismissPayment]);

	// The checked option owns the group's only tab stop, so arrow-key movement has to carry focus with
	// it: otherwise the tab stop moves to a row the reader cannot see themselves standing on.
	const {onKeyDown: onOptionKeyDown, selectedRef} = useRadioGroupKeys(variants, variant, setVariant);

	// The same derivation `useExitOffer` gates on, so the figures shown are the ones the gate found.
	// Null is unreachable while `open` is true — it is exactly that gate's last condition — and
	// rendering nothing is the right answer if a future storefront ever makes it reachable.

	if (amounts === null) {
		return null;
	}
	const introPrice = formatMoney(amounts.currency, amounts.intro, locale);
	// The regular price the CHOSEN term renews at: Plus's for the first-month term, Premium's otherwise.
	// The renewal line and the refusal notice state this figure; the payment form states its own term's.
	const monthlyPrice = formatMoney(amounts.currency, exitOfferMonthlyFor(amounts, variant), locale);
	// The term the payment form states: the one the session was minted for, which the reader may have
	// moved away from on the rows while the press was held or the session was minted (ui#3117).
	const paying = checkoutVariant ?? variant;
	const payingMonthlyPrice = formatMoney(amounts.currency, exitOfferMonthlyFor(amounts, paying), locale);
	// What the free trial's card hold reserves (kitecorp/transferify-api-service#2265): the TRIAL's own tier's
	// monthly price, whichever row is selected. `monthlyPrice` above follows the selection, so reusing it here
	// would quote Plus's price on the Premium trial's row while the Plus term is chosen.
	const trialHoldPrice = formatMoney(amounts.currency, exitOfferMonthlyFor(amounts, "trial7"), locale);
	// Each term's own title, which the payment form repeats as the terms the reader chose. The Plus term's
	// is absent on a day the api quotes none, which is also the day it is not on the menu.
	const titles: Record<PricingExitOfferVariant, string | undefined> = {
		intro14: exitOffer.intro.title(introPrice, EXIT_OFFER_INTRO_DAYS),
		firstMonth: amounts.firstMonth === null ? undefined
			: exitOffer.firstMonth.title(formatMoney(amounts.currency, amounts.firstMonth.amount, locale)),
		trial7: exitOffer.trial.title(EXIT_OFFER_TRIAL_DAYS),
	};
	// What each term's first payment is, which the order button names (kitecorp/transferify-ui#3394). The
	// trial's is a card hold rather than a charge, so its button names the monthly price.
	const firstCharges: Record<PricingExitOfferVariant, string | undefined> = {
		intro14: introPrice,
		firstMonth: amounts.firstMonth === null ? undefined : formatMoney(amounts.currency, amounts.firstMonth.amount, locale),
		trial7: undefined,
	};
	// The plan each row sells, named in the chip at its right end (owner review of ui#2994).
	const planOf = (term: PricingExitOfferVariant) => {
		const tier = exitOfferPlanFor(term);
		return {tier, label: exitOffer.planChip(pricing.planNames[tier])};
	};

	return (
		<OfferDialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			slots={{transition: SheetTransition}}
			slotProps={{
				transition: {onExited: observedExit},
				backdrop: {style: {background: overlay.scrim}},
				// The paper carries role="dialog", so the accessible name belongs there — the same
				// placement ModalSheet uses. This surface has no plain-text heading to point at.
				paper: {"aria-label": exitOffer.dialogAria},
			}}
		>
			<Layout>
				<Art data-testid="exit-offer-art">
					<ExitOfferArt/>
				</Art>
				<Content>
					<CloseButton type="button" onClick={onClose} aria-label={common.close}>✕</CloseButton>
					{clientSecret !== null ? <SubscriptionElementsCheckout
						plan={exitOfferPlanFor(paying)}
						clientSecret={clientSecret}
						collectsShippingAddress={collectsShippingAddress}
						contractDocument={contract.document}
						awaitingDocument={contract.awaitingDocument}
						consented={acknowledged}
						onConsentChange={checked => { setAcknowledged(checked); if (!checked) dismissPayment(); }}
						buyerEmail={user.email === "" ? undefined : user.email}
						monthlyPrice={payingMonthlyPrice}
						introTerms={offerUnavailable ? undefined : titles[paying]}
						firstCharge={offerUnavailable ? undefined : firstCharges[paying]}
						trialHold={trialHold}
						onPlaced={() => { completed(); onClose(); }}
					/> : <>
					<Eyebrow>{exitOffer.eyebrow}</Eyebrow>
					<Title>
						{exitOffer.titleLead}
						<TitleAccent>{exitOffer.titleAccent}</TitleAccent>
						{exitOffer.titleTrail}
					</Title>
					{offerUnavailable ? (
						// The terms are gone, so the radio rows go with them: leaving them on screen
						// would keep offering something the api has just refused.
						<OfferUnavailable role="status" data-testid="exit-offer-unavailable">
							{exitOffer.unavailable.lead}<strong>{monthlyPrice}</strong>
							{exitOffer.unavailable.trail}
						</OfferUnavailable>
					) : (
					<Options role="radiogroup" aria-label={exitOffer.optionsAria} onKeyDown={onOptionKeyDown}>
						<OfferOption
							selected={variant === "intro14"}
							onSelect={() => setVariant("intro14")}
							selectedRef={selectedRef}
							testId="exit-offer-intro14"
							title={exitOffer.intro.title(introPrice, EXIT_OFFER_INTRO_DAYS)}
							plan={planOf("intro14")}
						/>
						{titles.firstMonth !== undefined && <OfferOption
							selected={variant === "firstMonth"}
							onSelect={() => setVariant("firstMonth")}
							selectedRef={selectedRef}
							testId="exit-offer-firstMonth"
							title={titles.firstMonth}
							plan={planOf("firstMonth")}
						/>}
						<OfferOption
							selected={variant === "trial7"}
							onSelect={() => setVariant("trial7")}
							selectedRef={selectedRef}
							testId="exit-offer-trial7"
							title={exitOffer.trial.title(EXIT_OFFER_TRIAL_DAYS)}
							detail={exitOffer.trial.detail(trialHoldPrice)}
							plan={planOf("trial7")}
						/>
					</Options>
					)}
					<AfterNote>
						{exitOffer.then.lead}<strong>{monthlyPrice}</strong>{exitOffer.then.trail}
					</AfterNote>
					{/*
					  * The express consent, immediately above the control it gates (kitecorp/transferify-ui#1844).
					  * A SUBSCRIPTION, so the reader is shown the proportionate-payment statement rather than
					  * the retired one-off's: nothing here is fully performed inside 14 days, and telling them they
					  * lose the right outright would be untrue about their own rights. WHICH sentence that is
					  * comes from the rule module, never from a choice made in this file.
					  */}
					<ImmediateDeliveryConsent
						contractDocument={contract.document}
						// The CTA below already waits; the box does too, so the reader cannot agree to terms the
						// api has not yet named (ui#2562). It holds the box only while it is UNTICKED, so a
						// reader who has agreed keeps their own ability to change their mind.
						awaitingDocument={contract.awaitingDocument}
						effect={withdrawalEffectOf("subscription")}
						checked={acknowledged}
						onChange={setAcknowledged}
						testId="exit-offer-consent"
					/>
					{/*
					  * One CTA, two meanings, and the label is what tells them apart. The second press is
					  * DELIBERATE: nothing is charged by the refusal itself, so the reader gets to read the
					  * new price and decide again rather than being carried to it.
					  *
					  * Inert until the reader has agreed; the hook refuses the same press on the same verdict, so
					  * this is presentation of a rule rather than the rule itself. Nothing on screen explains the
					  * inertness any more (ui#1889 removed that note by owner decision), so the statement the
					  * reader ticks is the only thing left pointing at what is still owed.
					  */}
					<Continue
						type="button"
						disabled={redirecting || admission === "acknowledgementRequired" || contract.awaitingDocument}
						// Busy from the press on, including while it waits for a restored sign-in (ui#3117).
						aria-busy={redirecting}
						// The figure on the pressed term travels with the press (ui#1965), decided by the
						// rule module: intro on the fortnight, the discounted month on the Plus term, the
						// term's own monthly price once the offer is gone, none on the free trial.
						onClick={event => void (offerUnavailable
							? continueAtRegularPrice(variant, exitOfferRegularPriceFor(amounts, variant))
							: continueWithOffer(variant, exitOfferPriceFor(amounts, variant), event.currentTarget))}
					>
						{offerUnavailable ? exitOffer.unavailable.cta : exitOffer.cta}
					</Continue>
					{error !== null && <OfferUnavailable role="alert">{checkout.errors[error]}</OfferUnavailable>}
					</>}
				</Content>
			</Layout>
		</OfferDialog>
	);
}
